import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import type { AiChatSession } from '../../services/aiChatHistoryService'
import type { NlsKey } from '../../nls/strings_nls'

export type MemberAiHistoryGroup = {
  label: string
  labelKey: NlsKey
  sessions: AiChatSession[]
}

export const aiHistoryDisplayTitle = (raw: string): string => {
  if (!raw) return ''
  let text = raw.trim()
  // Repeatedly strip leading ATX markdown heading markers (#, ##, ..., ######) plus whitespace
  text = text.replace(/^(?:#{1,6}\s+)+/, '')
  // Strip surrounding ** or __ bold markers if the whole title is wrapped in them
  if ((text.startsWith('**') && text.endsWith('**') && text.length >= 4) ||
      (text.startsWith('__') && text.endsWith('__') && text.length >= 4)) {
    text = text.slice(2, -2).trim()
  }
  // Strip surrounding * or _ italic markers if the whole title is wrapped in them
  if ((text.startsWith('*') && text.endsWith('*') && text.length >= 2) ||
      (text.startsWith('_') && text.endsWith('_') && text.length >= 2)) {
    text = text.slice(1, -1).trim()
  }
  // Collapse internal whitespace runs to a single space and trim
  return text.replace(/\s+/g, ' ').trim()
}

export const millis = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (value instanceof Date) return value.getTime()
  if (value && typeof value === 'object' && 'toMillis' in value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis()
  }
  return 0
}

export const firstUserTurn = (session: AiChatSession) =>
  session.turns.find((turn) => turn.role === 'USER')?.text ?? ''

export const lastAssistantTurn = (session: AiChatSession) =>
  session.turns.filter((turn) => turn.role === 'ASSISTANT').at(-1)?.text ?? ''

export const liveAiHistorySessions = (sessions: AiChatSession[]) =>
  sessions.filter((session) => session.status !== 'ARCHIVED' && session.title.trim() && session.title !== 'New chat')

export const sessionMatchesAiHistoryQuery = (session: AiChatSession, query: string) => {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const haystack = [
    session.title,
    firstUserTurn(session),
    ...session.attachments.map((attachment) => attachment.label),
  ].join(' ').toLowerCase()
  return needle.split(/\s+/).every((part) => haystack.includes(part))
}

export const groupAiHistorySessions = (sessions: AiChatSession[], now = Date.now()): MemberAiHistoryGroup[] => {
  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)
  const today = startOfToday.getTime()
  const yesterday = today - 24 * 60 * 60 * 1000
  const week = today - 6 * 24 * 60 * 60 * 1000
  const groups: MemberAiHistoryGroup[] = [
    { label: 'Today', labelKey: 'NLSMemberAiHistoryGroupToday', sessions: [] },
    { label: 'Yesterday', labelKey: 'NLSMemberAiHistoryGroupYesterday', sessions: [] },
    { label: 'This week', labelKey: 'NLSMemberAiHistoryGroupThisWeek', sessions: [] },
    { label: 'Older', labelKey: 'NLSMemberAiHistoryGroupOlder', sessions: [] },
  ]
  for (const session of sessions) {
    const updatedAt = millis(session.lastMessageAt ?? session.updatedAt ?? session.createdAt)
    if (updatedAt >= today) groups[0].sessions.push(session)
    else if (updatedAt >= yesterday) groups[1].sessions.push(session)
    else if (updatedAt >= week) groups[2].sessions.push(session)
    else groups[3].sessions.push(session)
  }
  return groups.filter((group) => group.sessions.length > 0)
}

export const sourcePillsForSession = (session: AiChatSession) =>
  session.attachments.slice(0, 3).map((attachment) => {
    if (attachment.kind === 'chat') {
      return { id: attachment.id, kind: attachment.kind, label: `${attachment.messages?.length ?? 0} messages`, Icon: ChatBubbleOutlineRoundedIcon }
    }
    if (attachment.kind === 'timeline') {
      return { id: attachment.id, kind: attachment.kind, label: attachment.label, Icon: TrackChangesRoundedIcon }
    }
    return { id: attachment.id, kind: attachment.kind, label: attachment.label, Icon: AttachFileRoundedIcon }
  })
